import React, { useState, useEffect } from "react";
import {
  Box,
  Modal,
  Divider,
  IconButton,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import Tabs from "../WheelGallery/GallerySettings/children/Tabs";
import GalleryThemeStudio from "../GalleryTheme/GalleryThemeStudio";
import BusinessBoxThemeSection from "./BusinessBoxThemeSection";
import LinkVaultThemeSection from "./LinkVaultThemeSection";

/**
 * QA-112 follow-up (Gordon 2026-08-12): the avatar/profile dropdown's
 * "Themes" row (ProfileMenu/index.jsx) now opens THIS popup directly, in
 * place, from anywhere the menu is mounted (Gallery header, Wheel builder
 * header, ...) -- replacing the old "Gallery Theme" row, which worked ONLY
 * by navigating to `/gallery/:id?theme=gallery` and relying on
 * WheelGallery/Profile/index.jsx's query-param effect to reopen
 * GallerySettings tab "2". That indirection meant clicking it from the
 * builder (or anywhere off the gallery page) yanked the user away to their
 * gallery first. This popup needs no navigation.
 *
 * SHELL REUSE: this mirrors the exact modal chrome + tab pattern Business
 * Box (GallerySettings' `showBusinessViewOnly` mode) and LinkVault
 * (GallerySettings tab "1") already use -- see
 * ../WheelGallery/GallerySettings/index.jsx: a centered MUI <Modal> with a
 * sticky header (icon + title + 44px close button), a left vertical Tabs
 * rail, a right content Grid. The Tabs rail below imports that SAME `Tabs`
 * child component verbatim (not reimplemented); only the modal's outer
 * <Box> styling is duplicated (GallerySettings hardcodes its own unrelated
 * tab list -- Profile Details / LinkVault / Gallery Theme -- so it can't be
 * reused wholesale for a Themes-only popup without dragging those in).
 *
 * SECTIONS ARRAY was the seam for what's next -- Business Box theme and
 * LinkVault theme (gordon-0917-themes-popup-add-business-box-and-linkvault)
 * now land here as sibling { value, label, icon, render } entries, exactly as
 * the original seam comment described: each renders its EXISTING controls
 * (BusinessBoxThemeSection / LinkVaultThemeSection, which themselves compose
 * the SAME Theming/ThemeEditor + save mutations InnerForm's own "Theme" tab
 * and LinkVault's own "Vault Design" tab already use) -- not re-derived or
 * rewritten editors.
 *
 * ICON PARITY (Gordon 2026-09-18, gordon-0918-themes-modal-side-pane-icons-
 * accent-borders-corners-linkvault-bb): all three section icons must be the
 * SAME size, left-justified, line-art. MEASURED on web-dev.iyuga.net
 * (main.9de95baea.js): the old LinkVault icon was `ReactComponent` imported
 * straight from an SVG file with its own hardcoded `width="24" height="25"`
 * attributes AND a baked-in filled purple circle -- Tabs.jsx's
 * `<Icon sx={{fontSize:16,width:16,height:16}} />` only sizes real MUI
 * SvgIcon components (sx is an emotion/MUI prop; a plain imported <svg> tag
 * doesn't understand it and keeps its file's own width/height), so the
 * LinkVault tab icon rendered at a different box size than Gallery Theme/
 * Business Box's true MUI icons, throwing the row's left edge out of
 * alignment. Swapping to MUI's own `LinkOutlined` (same component family as
 * the other two, same sx sizing mechanism, genuinely line-art/outlined like
 * StorefrontOutlined) fixes the size/alignment AND the line-art ask in one
 * move, instead of hand-tuning the bespoke asset. ColorLensRounded (a FILLED
 * glyph) is swapped for ColorLensOutlined so all three read as the same
 * visual family. Tabs.jsx also gets a defensive fixed-size icon slot (belt
 * and suspenders) so a future non-MUI icon can never regress this again.
 *
 * G-161 (measured before writing this): the old row's onClick was
 * `nav(\`/gallery/${id}?theme=gallery\`)`, picked up by
 * WheelGallery/Profile/index.jsx (~line 262) to open GallerySettings with
 * tabIdx="2", whose tab-2 content (WheelGallery/GallerySettings/index.jsx
 * ~line 256) is exactly `<GalleryThemeStudio inModal userId={...}
 * profile={...} displayName={...} handle={...} />`. The Gallery Theme
 * section below mounts that SAME component with the SAME prop shape --
 * not a re-derived or re-guessed editor.
 */
const SECTIONS = [
  {
    value: "gallery",
    label: "Gallery Theme",
    icon: ColorLensOutlinedIcon,
    render: (userProfile) => (
      <GalleryThemeStudio
        inModal
        userId={userProfile?.id}
        profile={userProfile}
        displayName={
          [userProfile?.first_name, userProfile?.last_name]
            .filter(Boolean)
            .join(" ") || "Your Gallery"
        }
        handle={userProfile?.username || "you"}
        modalHeight={{ xs: "100%", md: "72vh" }}
      />
    ),
  },
  {
    value: "business-box",
    label: "Business Box",
    icon: StorefrontOutlinedIcon,
    render: () => <BusinessBoxThemeSection />,
  },
  {
    value: "link-vault",
    label: "LinkVault",
    icon: LinkOutlinedIcon,
    render: () => <LinkVaultThemeSection />,
  },
];

const ThemesPopup = ({ open, onClose, userProfile, initialSection }) => {
  const [selectedTab, setSelectedTab] = useState(initialSection || SECTIONS[0].value);
  // A deep-link entry (Business Box / LinkVault kebab "Theme" row) sets
  // initialSection fresh on every open via redux (see redux/reducers/gallery
  // .js themesPopup.section) -- re-sync whenever the popup opens so it always
  // lands on the section that was actually clicked, not whatever section was
  // last selected the previous time it was open.
  useEffect(() => {
    if (open && initialSection) {
      setSelectedTab(initialSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSection]);
  const active = SECTIONS.find((s) => s.value === selectedTab) || SECTIONS[0];
  // BUG FIX (ISP-9999, gallery-theme-modal-clips-390): this popup duplicates
  // GallerySettings' modal chrome (see file banner) but never picked up that
  // component's QA-1191 fix — width stayed a flat "70vw" (273px at a 390px
  // phone) and the tabs Grid stayed a fixed xs={2}/xs={10} split, so the
  // rail+content never had room to reflow. Below `md` widen the modal and
  // stack the (currently single) section tab on top, full width, exactly like
  // GallerySettings/index.jsx already does.
  const theme = useTheme();
  const isNarrowTabs = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="themes-popup-title"
    >
      <Box
        // BUG FIX (nem-themes-dropdown-leak-on-preset-select): this Modal's
        // content is portaled to document.body, but ThemesPopup is still a JSX
        // child of ProfileMenu's Styled.UserContainer, whose onClick toggles
        // the avatar dropdown open/closed. React bubbles synthetic events
        // along the COMPONENT tree (not the DOM tree) across portals, so any
        // click inside this modal -- e.g. a preset swatch -- was reaching that
        // outer onClick and reopening the dropdown over the modal. Stop it
        // here so nothing inside the popup can ever toggle an ancestor menu.
        onClick={(e) => e.stopPropagation()}
        // BUG FIX (ISP-9999, help-fab-overlaps-save-pill-390): this popup uses
        // a bare MUI <Modal> (not <Dialog>), so its content never carried
        // role="dialog"/aria-modal="true". HelpAssistant's useBlockingModalOpen
        // hides the global Help FAB whenever a real blocking modal is open
        // (BUG-explore-398d6d), matched via
        // `.MuiDialog-root, [role="dialog"][aria-modal="true"]` -- lacking
        // both, this popup fell outside that selector, so the FAB never
        // learned it was open and kept painting over the footer's Save/Saved
        // pill at <=390px (measured live web-stg: FAB {318,772,56x56} vs
        // Saved pill {273.6,762.9,85.7x39}, 41.3x29.9px real overlap,
        // elementFromPoint at the pill's own right edge hit the FAB). Adding
        // the ARIA a modal is supposed to carry is the fix AND closes a real
        // a11y gap (screen readers were never told this was a dialog) --
        // useBlockingModalOpen's selector already matches this pattern
        // generically, so nothing else needs to change.
        role="dialog"
        aria-modal="true"
        aria-labelledby="themes-popup-title"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "#FFFFFF",
          boxShadow: 24,
          width: { xs: "94vw", md: "70vw" },
          border: "1px solid #E1E1E1",
          borderRadius: "8px",
          maxHeight: "90vh",
          overflow: "hidden",
          // BUG FIX T22.576 (Gordon S23+ Chrome, measured web-stg 384x780
          // main.31c24220): below md the frame is header (85) + tab row (58)
          // + 40px padding + the studio's own fixed 72vh (562) = ~765px inside
          // a 702px (90vh) frame with overflow:hidden, so the studio's footer
          // (Discard/Save) was cut 24px below the frame and the scroll area
          // that holds the Live preview was squeezed; Business Box / LinkVault
          // had no scroll container at all. Below md the frame is now a
          // definite-height flex column: header + tabs keep their size and the
          // section body takes the rest (studio fills it, other sections
          // scroll inside it). 90dvh where supported: Android Chrome's 90vh is
          // measured with the URL bar hidden, taller than the visible area.
          [theme.breakpoints.down("md")]: {
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            "@supports (height: 100dvh)": { height: "90dvh", maxHeight: "90dvh" },
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <PaletteOutlinedIcon aria-hidden="true" sx={{ color: "#323232" }} />
            <Typography
              id="themes-popup-title"
              sx={{
                color: "#0F0F0F",
                fontSize: "24px",
                fontWeight: "700",
                lineHeight: "20px",
                fontFamily: "DM Sans",
              }}
            >
              Themes
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close Themes"
            sx={{
              minWidth: 44,
              minHeight: 44,
              "&:focus-visible": {
                outline: "2px solid #9B5AF8",
                outlineOffset: "2px",
              },
            }}
          >
            <CloseIcon aria-hidden="true" sx={{ color: "#323232" }} />
          </IconButton>
        </Box>

        <Divider sx={{ width: "100%", backgroundColor: "#BBBBBB", flexShrink: 0 }} />

        <Grid
          spacing={0}
          sx={{
            padding: "20px 0px",
            [theme.breakpoints.down("md")]: {
              padding: "12px 0px 0px",
              flexDirection: "column",
              flexWrap: "nowrap",
              flex: "1 1 0",
              minHeight: 0,
            },
          }}
          container
        >
          <Grid
            item
            xs={12}
            md={2}
            sx={{ [theme.breakpoints.down("md")]: { flexBasis: "auto", flexShrink: 0 } }}
          >
            <Tabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              tabList={SECTIONS.map(({ value, label, icon }) => ({
                value,
                label,
                icon,
              }))}
              orientation={isNarrowTabs ? "horizontal" : "vertical"}
              borderTop={isNarrowTabs}
              fitNarrow
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={10}
            data-testid="themes-popup-body"
            sx={{
              [theme.breakpoints.down("md")]: {
                flex: "1 1 0",
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {active.render(userProfile)}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default ThemesPopup;
